import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../singleton/axios';

export interface EarningsRecord {
  symbol: string;
  date: string;
  epsActual: number | null;
  epsEstimated: number | null;
  revenueActual: number | null;
  revenueEstimated: number | null;
  lastUpdated: string;
}

export interface CompanyEarningsResponse {
  data: EarningsRecord[];
  symbol: string;
  totalRecords: number;
}

export interface EarningsChartData {
  date: string;
  epsActual: number | null;
  epsEstimated: number | null;
  beatStatus: 'beat' | 'miss' | 'inline' | 'upcoming' | null;
  beatPercentage: number | null;
}

// Helper function to determine beat/miss status
const calculateBeatStatus = (actual: number | null, estimated: number | null): 'beat' | 'miss' | 'inline' | 'upcoming' | null => {
  if (actual === null && estimated === null) return null;
  if (actual === null) return 'upcoming';
  if (estimated === null) return null;
  
  const tolerance = 0.01; // 1 cent tolerance for "inline"
  const diff = actual - estimated;
  
  if (Math.abs(diff) <= tolerance) return 'inline';
  return diff > 0 ? 'beat' : 'miss';
};

// Helper function to calculate beat percentage
const calculateBeatPercentage = (actual: number | null, estimated: number | null): number | null => {
  if (actual === null || estimated === null || estimated === 0) return null;
  return ((actual - estimated) / Math.abs(estimated)) * 100;
};

// Transform raw earnings data for chart consumption
export const transformEarningsData = (data: EarningsRecord[]): EarningsChartData[] => {
  return data.map(record => ({
    date: record.date,
    epsActual: record.epsActual,
    epsEstimated: record.epsEstimated,
    beatStatus: calculateBeatStatus(record.epsActual, record.epsEstimated),
    beatPercentage: calculateBeatPercentage(record.epsActual, record.epsEstimated),
  }));
};

export const useCompanyEarnings = (symbol: string | undefined, limit: number = 12) => {
  return useQuery({
    queryKey: ['companyEarnings', symbol, limit],
    queryFn: async () => {
      if (!symbol) {
        throw new Error('Symbol is required');
      }

      const response = await api.get<CompanyEarningsResponse>('/company-earnings', {
        params: {
          symbol: symbol.toUpperCase(),
          limit,
        },
      });

      return response.data;
    },
    enabled: !!symbol,
    staleTime: 2.5 * 60 * 1000, // 2.5 minutes for fresh earnings data
    gcTime: 1000 * 60 * 30, // 30 minutes (was cacheTime in v3)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Custom hook for chart-ready data
export const useCompanyEarningsChart = (symbol: string | undefined, limit: number = 12) => {
  const { data, isLoading, error, refetch } = useCompanyEarnings(symbol, limit);

  const chartData = data ? transformEarningsData(data.data) : [];

  return {
    chartData,
    rawData: data,
    isLoading,
    error,
    refetch,
    symbol: data?.symbol,
    totalRecords: data?.totalRecords,
  };
};
